import {RefractoryClassificationClass} from '@domain/refractory-product-catalog'
import {ControlledAutocompleteSelect, ControlledInput} from '@hm/fundamental'
import {useState} from 'react'
import {FieldArrayWithId, Controller, Control} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

import {useRefractoriesOptions} from '@refra/modules/common/hooks/queries'
import {useBrickShapesQuery} from '@refra/modules/refra-planning/hooks/queries'
import {StaticPartLayerTypes} from '@refra/modules/refra-static-parts/enums'
import {useEditReplacement} from '@refra/modules/refra-static-parts/providers'
import {ReplacementFormDTO, LayerFormDTO} from '@refra/modules/refra-static-parts/types'

interface LayerFormControlProps {
  materialInstallationIndex: number
  layerIndex: number
  layerField: FieldArrayWithId<ReplacementFormDTO, `materialInstallations.${number}.layers`>
  control: Control<ReplacementFormDTO>
  watchedLayer: LayerFormDTO | undefined
  area: number
  materialWeight: number
}

export const LayerFormControl = ({
  materialInstallationIndex,
  layerIndex,
  layerField,
  control,
  watchedLayer,
  area,
  materialWeight
}: LayerFormControlProps) => {
  const {t} = useTranslation()
  const [refractoriesSearch, setRefractoriesSearch] = useState('')
  const {activeAfter, assignedRefractoriesMap} = useEditReplacement()
  const {refractoriesOptions, refractoriesMap} = useRefractoriesOptions({
    refractoriesSearch,
    activeAfter,
    enabled: Boolean(activeAfter)
  })
  const {data: brickShapes} = useBrickShapesQuery()

  const brickShapesOptions =
    brickShapes?.map((shape) => ({
      label: shape.name,
      value: shape.name
    })) ?? []

  const refractoryId = watchedLayer?.refractoryId
  const selectedRefractory =
    refractoryId !== undefined && refractoryId !== null
      ? (refractoriesMap[refractoryId] ?? assignedRefractoriesMap[refractoryId])
      : undefined
  const isBrick = selectedRefractory?.classification?.class === RefractoryClassificationClass.Brick
  const materialOptions =
    selectedRefractory &&
    !refractoriesOptions.some((option) => option.value === selectedRefractory.id)
      ? [{value: selectedRefractory.id, label: selectedRefractory.name}, ...refractoriesOptions]
      : refractoriesOptions

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-3">
        <Controller
          name={`materialInstallations.${materialInstallationIndex}.layers.${layerIndex}.refractoryId`}
          control={control}
          rules={{
            required:
              layerField.layerType === StaticPartLayerTypes.TOP_LAYER ? t('error.required') : false
          }}
          render={({field: controllerField, fieldState: {error}}) => (
            <ControlledAutocompleteSelect
              label={t(`staticParts.editReplacement.layers.${layerField.layerType}`)}
              options={materialOptions}
              debounce
              selectedValues={[controllerField.value ?? '']}
              error={error?.message}
              isTouched={!!error?.message}
              isDisabled={!activeAfter}
              onFilterChange={(query) => setRefractoriesSearch(query ?? '')}
              onChange={(refractoryId) => {
                controllerField.onChange(refractoryId[0])
              }}
              data-cy={`material_installation_${layerIndex}_select`}
            />
          )}
        />

        {selectedRefractory && !isBrick && (
          <div className="flex flex-col gap-3">
            <Controller
              name={`materialInstallations.${materialInstallationIndex}.layers.${layerIndex}.bricksInfo.thickness`}
              control={control}
              rules={{required: t('error.required')}}
              render={({field: controllerField, fieldState: {error}}) => (
                <ControlledInput
                  label={t('staticParts.editReplacement.table.thickness')}
                  value={controllerField.value?.toString() ?? ''}
                  onChange={(e) => controllerField.onChange(parseFloat(e.target.value))}
                  error={error?.message}
                  isTouched={!!error?.message}
                  type="number"
                  min={0}
                  step={1}
                  data-cy={`material_thickness_${layerIndex}_input`}
                />
              )}
            />
          </div>
        )}
      </div>

      {selectedRefractory && isBrick && (
        <div className="grid grid-cols-12 gap-4 items-center">
          <div className="col-span-8">
            <Controller
              name={`materialInstallations.${materialInstallationIndex}.layers.${layerIndex}.bricksInfo.brickShape`}
              control={control}
              rules={{required: t('error.required')}}
              render={({field: controllerField, fieldState: {error}}) => (
                <ControlledAutocompleteSelect
                  label={t('staticParts.editReplacement.table.brickShape')}
                  options={brickShapesOptions}
                  selectedValues={[controllerField.value ?? '']}
                  error={error?.message}
                  isTouched={!!error?.message}
                  onChange={(values) => {
                    controllerField.onChange(values[0])
                  }}
                  data-cy={`material_brick_shape_${layerIndex}_select`}
                />
              )}
            />
          </div>
          <div className="col-span-4">
            <Controller
              name={`materialInstallations.${materialInstallationIndex}.layers.${layerIndex}.bricksInfo.numberOfBricks`}
              control={control}
              rules={{required: t('error.required'), min: 1}}
              render={({field: controllerField, fieldState: {error}}) => (
                <ControlledInput
                  label={t('staticParts.editReplacement.table.quantity')}
                  value={controllerField.value?.toString() ?? ''}
                  onChange={(e) => controllerField.onChange(parseInt(e.target.value))}
                  error={error?.message}
                  isTouched={!!error?.message}
                  type="number"
                  min={0}
                  step={1}
                  data-cy={`material_quantity_${layerIndex}_input`}
                />
              )}
            />
          </div>
        </div>
      )}

      {selectedRefractory && (
        <div className="grid grid-cols-3 gap-4 px-1">
          <div className="flex flex-col">
            <span className="text-xs text-neutral_faded_60">
              {t('staticParts.editReplacement.sections.selectedArea')}
            </span>
            <span className="text-sm font-semibold">{area.toFixed(2)} m²</span>
          </div>
          <div className="flex flex-col">
            <span className="text-xs text-neutral_faded_60">
              {t('staticParts.editReplacement.sections.materialAmount')}
            </span>
            <span className="text-sm font-semibold">
              {materialWeight !== undefined && materialWeight > 0 ? `${materialWeight} t` : '-'}
            </span>
          </div>
          <div className="flex flex-col">
            <span className="text-xs text-neutral_faded_60">
              {t('staticParts.editReplacement.sections.cost')}
            </span>
            <span className="text-sm font-semibold">-</span>
          </div>
        </div>
      )}
    </div>
  )
}
